import { OcrConfig, ImageDataLike } from './ocr-types'

function applyContrastStretch(rgba: Uint8ClampedArray, count: number): void {
  let min = 255, max = 0
  for (let i = 0; i < count; i++) {
    const idx = i * 4
    const gray = ((rgba[idx] ?? 0) + (rgba[idx + 1] ?? 0) + (rgba[idx + 2] ?? 0)) / 3
    if (gray < min) min = gray
    if (gray > max) max = gray
  }
  if (max - min < 5) return
  const range = max - min
  for (let i = 0; i < count; i++) {
    const idx = i * 4
    const gray = ((rgba[idx] ?? 0) + (rgba[idx + 1] ?? 0) + (rgba[idx + 2] ?? 0)) / 3
    const stretched = ((gray - min) / range) * 255
    rgba[idx] = stretched
    rgba[idx + 1] = stretched
    rgba[idx + 2] = stretched
  }
}

export function preprocessImage(imageData: ImageDataLike, config: OcrConfig): Float32Array {
  const { data: pixels, width: origW, height: origH } = imageData

  if (origW <= 0 || origH <= 0 || !Number.isFinite(origW) || !Number.isFinite(origH)) {
    throw new Error(`Invalid image dimensions: ${origW}x${origH}`)
  }

  const targetH = config.imgH
  const targetW = config.imgW
  const scale = targetH / origH
  const scaledW = Math.max(1, Math.round(origW * scale))

  const canvas = document.createElement('canvas')
  canvas.width = targetW
  canvas.height = targetH
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error('Could not create 2D context for image preprocessing')
  }

  const sourceCanvas = document.createElement('canvas')
  sourceCanvas.width = origW
  sourceCanvas.height = origH
  const sourceCtx = sourceCanvas.getContext('2d')
  if (!sourceCtx) {
    throw new Error('Could not create source 2D context')
  }
  const sourceImageData = new ImageData(new Uint8ClampedArray(pixels), origW, origH)

  let sourceMean = 0
  for (let i = 0; i < sourceImageData.data.length; i += 4) {
    sourceMean += (sourceImageData.data[i]! + sourceImageData.data[i + 1]! + sourceImageData.data[i + 2]!) / 3
  }
  sourceMean /= (sourceImageData.data.length / 4)

  if (sourceMean < 128) {
    for (let i = 0; i < sourceImageData.data.length; i += 4) {
      sourceImageData.data[i] = 255 - sourceImageData.data[i]!
      sourceImageData.data[i + 1] = 255 - sourceImageData.data[i + 1]!
      sourceImageData.data[i + 2] = 255 - sourceImageData.data[i + 2]!
    }
  }

  sourceCtx.putImageData(sourceImageData, 0, 0)

  ctx.fillStyle = 'rgb(128, 128, 128)'
  ctx.fillRect(0, 0, targetW, targetH)
  ctx.drawImage(sourceCanvas, 0, 0, scaledW, targetH)

  const resultData = ctx.getImageData(0, 0, targetW, targetH)
  applyContrastStretch(resultData.data, targetH * targetW)

  const tensorSize = targetH * targetW
  const tensor = new Float32Array(tensorSize)
  const rgba = resultData.data

  for (let i = 0; i < tensorSize; i++) {
    const pixel = (rgba[i * 4] ?? 0) / 255.0
    tensor[i] = (pixel - 0.5) / 0.5
  }

  if ((window as any).__OCR_DEBUG__) {
    const sampleSize = 8
    console.groupCollapsed(`[OCR Preprocess] ${origW}x${origH} → ${targetW}x${targetH} (scale: ${scale.toFixed(2)}, scaledW: ${scaledW}, invert: ${sourceMean < 128})`)
    console.log('Source mean:', sourceMean.toFixed(1), sourceMean < 128 ? '(inverted)' : '(original)')
    console.log('Tensor first:', Array.from(tensor.slice(0, sampleSize)).map(v => v.toFixed(3)))
    console.log('Tensor last:', Array.from(tensor.slice(Math.max(0, tensorSize - sampleSize))).map(v => v.toFixed(3)))
    console.log('Tensor stats:', {
      min: Math.min(...tensor).toFixed(3),
      max: Math.max(...tensor).toFixed(3),
      mean: (tensor.reduce((a, b) => a + b, 0) / tensorSize).toFixed(3),
      scaledW,
      crop: scaledW > targetW ? 'right' : 'none',
      pad: scaledW < targetW ? 'right' : 'none',
    })
    console.groupEnd()
  }

  return tensor
}
